import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import React, { useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import {
  HeadingsRow,
  PageHeading,
  SrOnly,
  TeamHeading,
} from '@/components/Layout/Parts';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import { ChannelGroupResource } from '@/types/resources';
import { api } from '@/utils/api';

import SettingsNavigation from '../components/SettingsNavigation';
import GroupForm from './GroupForm';
import GroupsTableRow from './GroupsTableRow';

const StyledTable = styled(Table)`
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  th,
  td {
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
      width: 64px;
    }
  }
  td {
    padding-top: 16px;
    padding-bottom: 16px;
  }
`;

const Groups = () => {
  const [showCreationForm, setShowCreationForm] = useState(false);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: groups,
    isLoading,
    isError,
  } = useQuery(
    [
      'channel_groups',
      {
        ...(!!debouncedSearch && { debouncedSearch }),
      },
    ],
    () =>
      api.get<never, ChannelGroupResource[]>(
        '/v1/distribution/channel-groups',
        {
          params: {
            ...(!!debouncedSearch && { 'filter[search]': debouncedSearch }),
          },
        },
      ),
    {
      staleTime: 120 * 1000,
    },
  );

  return (
    <>
      <HeadingsRow>
        <div>
          <TeamHeading />
          <PageHeading>Settings</PageHeading>
        </div>
        <Button onClick={() => setShowCreationForm(true)}>Create Group</Button>
      </HeadingsRow>
      <SettingsNavigation />
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by group name & channel"
      />
      <StyledTable $fixed>
        <thead>
          <tr>
            <Th>Group Name</Th>
            <Th>Channels</Th>
            <Th $actionsCol>
              <SrOnly>Actions</SrOnly>
            </Th>
          </tr>
        </thead>
        <tbody>
          <TableContent
            colSpan={3}
            isLoading={isLoading}
            hasData={!!groups?.length}
            isError={isError}
          >
            {groups?.map((group) => (
              <GroupsTableRow key={group.id} group={group} />
            ))}
          </TableContent>
        </tbody>
      </StyledTable>
      <AnimatePresence>
        {!!showCreationForm && (
          <GroupForm onClose={() => setShowCreationForm(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default Groups;
